// The bell, top right (Features 1, 20 Oct): what the board raised for the user, newest first, today and earlier, each
// opening what it is about. Read, read all; dismiss one or all, with a Dismissed view to bring one back (Round 6);
// snooze for later; done ticks the task it is about; and a reminder set from here in a few words and a time.

import { useEffect, useMemo, useRef, useState } from "react";
import { boardWords } from "../../shared/authors";
import { AlarmClock, Bell as BellIcon, CalendarClock, Check, CheckCheck, ChevronRight, Clock, Flag, MessageCircleQuestion, Plus, Send, Sparkles, TrendingUp, Trash2, Video, X } from "lucide-react";
import type { Notice, NoticeKind, Repeat } from "../../shared/notify";
import { cx } from "../lib/util";
import { Linked } from "../lib/Linked";
import { useShallow } from "zustand/react/shallow";
import { pageTitle } from "../../shared/entries";
import { blockerOf } from "../../shared/plan";
import { sinceWords } from "../../shared/format";
import { addReminder, allShownKeys, blockedRows, cancelReminder, dismiss, dismissedRows, doneNotice, markRead, noticeKeys, openNotice, shownNotices, snooze, snoozeTimes, undismiss, useFolderWaiting, useUnread, useWaitingOn, waitingKeys, type BlockedRow } from "../state/notices";
import { get, set, toast, useStore } from "../state/store";
import { openPlace, placeName } from "../state/place";

const ICON: Record<NoticeKind, typeof BellIcon> = {
  reminder: AlarmClock,
  post: Send,
  followup: Flag,
  countdown: Clock,
  threshold: TrendingUp,
  agent: Sparkles,
  question: MessageCircleQuestion,
  event: CalendarClock,
};

const timeOf = (t: number) => new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const dayOf = (t: number) => new Date(t).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });

/** A count in a small red circle (a pill from 10), the same on every theme; 99+ past that. */
export function CountBadge({ n, label }: { n: number; label?: string }) {
  if (!n) return null;
  return (
    <span className={cx("count-badge", n > 9 && "is-wide")} aria-label={label ?? `${n} waiting`} title={label ?? `${n} waiting`}>
      {n > 99 ? "99+" : n}
    </span>
  );
}

/**
 * A count that dismisses what it counts (Round 6): its number, and an × in its place on hover. A button of its own
 * where it stands on its own (the boards list); inside a tile that is a button already (All boards), a part of it
 * that takes the click for itself.
 */
function DismissCount({ n, label, keys, inButton = false }: { n: number; label: string; keys: () => string[]; inButton?: boolean }) {
  if (!n) return null;
  const go = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dismiss(keys());
  };
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const inner = (
    <>
      <span className="count-badge-n">{n > 99 ? "99+" : n}</span>
      <X size={11} strokeWidth={3} className="count-badge-x" aria-hidden />
    </>
  );
  const props = { className: cx("count-badge is-dismiss", n > 9 && "is-wide"), "aria-label": `${label}. Dismiss`, "data-tip": "Dismiss", title: undefined };
  return inButton ? (
    <span {...props} role="button" tabIndex={-1} onClick={go} onPointerDown={stop} onMouseDown={stop}>
      {inner}
    </span>
  ) : (
    <button type="button" {...props} onClick={go}>
      {inner}
    </button>
  );
}

/** What waits on the user on a board, beside its name in the boards list, All boards and a phone's list. */
export function BoardCount({ boardId, dismissable }: { boardId: string; /** a click dismisses what it counts (Round 6) */ dismissable?: "button" | "in-button" }) {
  const n = useWaitingOn(boardId);
  if (dismissable) return <DismissCount n={n} label={`${n} waiting on you`} keys={() => waitingKeys(get(), boardId)} inButton={dismissable === "in-button"} />;
  return <CountBadge n={n} label={`${n} waiting on you`} />;
}

/** A folder's total, so a closed folder still says something waits inside; a click dismisses it all (Round 6). */
export function FolderCount({ folderId, dismissable }: { folderId: string; dismissable?: boolean }) {
  const n = useFolderWaiting(folderId);
  if (dismissable) return <DismissCount n={n} label={`${n} waiting on you in this folder`} keys={() => get().boards.filter((b) => b.folder === folderId).flatMap((b) => waitingKeys(get(), b.id))} inButton />;
  return <CountBadge n={n} label={`${n} waiting on you in this folder`} />;
}

function Row({ n }: { n: Notice }) {
  const [later, setLater] = useState(false);
  const Icon = ICON[n.kind] ?? BellIcon;
  const where = n.to ? placeName(n.to) : null;
  return (
    <div className={cx("bell-row", !n.read && "is-unread")}>
      <button className="bell-row-main" onClick={() => openNotice(n)} disabled={!n.to && n.read}>
        <span className="bell-ico">
          <Icon size={14} />
        </span>
        <span className="bell-words">
          <span className="bell-title">{boardWords(n.title)}</span>
          {n.body && <span className="bell-body">{n.body}</span>}
          <span className="bell-meta">
            {timeOf(n.t)}
            {where ? ` · ${where}` : ""}
          </span>
        </span>
        {n.to && <ChevronRight size={14} className="bell-go" aria-hidden />}
      </button>
      {/* a meeting's reminder (Calendar step 11): Join in view at all times, not only with the row's tools */}
      {n.join && (
        <a className="btn btn-xs bell-join" href={n.join} target="_blank" rel="noreferrer" data-tip="Join the call">
          <Video size={12} /> Join
        </a>
      )}
      <span className="bell-tools">
        {n.done?.reminder && (
          <button aria-label="Done" data-tip="Done" onClick={() => doneNotice(n.id)}>
            <Check size={13} />
          </button>
        )}
        <button aria-label="Snooze" data-tip="Later" className={cx(later && "is-on")} onClick={() => setLater(!later)}>
          <AlarmClock size={13} />
        </button>
        <button aria-label="Dismiss" data-tip="Dismiss" onClick={() => dismiss(noticeKeys(n))}>
          <X size={13} />
        </button>
      </span>
      {later && (
        <div className="bell-later" role="menu" aria-label="Snooze until">
          {snoozeTimes().map((s) => (
            <button key={s.label} role="menuitem" onClick={() => (setLater(false), snooze(n.id, s.at))}>
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** A reminder in a few words and a time, from the bell. */
function RemindMe({ onDone }: { onDone: () => void }) {
  const [text, setText] = useState("");
  const soon = new Date(Date.now() + 60 * 60_000);
  soon.setMinutes(0, 0, 0);
  const local = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  const [when, setWhen] = useState(local(soon));
  const [repeat, setRepeat] = useState<"" | Repeat>("");
  const [busy, setBusy] = useState(false);
  const board = useStore((s) => s.boardId);
  return (
    <form
      className="bell-remind"
      onSubmit={(e) => {
        e.preventDefault();
        const at = new Date(when).getTime();
        if (!text.trim() || !Number.isFinite(at)) return;
        setBusy(true);
        addReminder({ text: text.trim(), at, repeat: repeat || null, to: board ? { board } : null })
          .then(() => {
            toast({ level: "success", title: `Reminder set for ${dayOf(at)}, ${timeOf(at)}`, body: text.trim() });
            onDone();
          })
          .catch((err: Error) => toast({ level: "error", title: "That reminder could not be set", body: err.message }))
          .finally(() => setBusy(false));
      }}
    >
      <input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Remind me to…" aria-label="What to remind you of" maxLength={200} onKeyDown={(e) => e.stopPropagation()} />
      <span className="bell-remind-row">
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} aria-label="When" onKeyDown={(e) => e.stopPropagation()} />
        <select value={repeat} onChange={(e) => setRepeat(e.target.value as "" | Repeat)} aria-label="Repeat">
          <option value="">Once</option>
          <option value="daily">Every day</option>
          <option value="weekdays">Weekdays</option>
          <option value="weekly">Every week</option>
          <option value="monthly">Every month</option>
        </select>
        <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !text.trim()}>
          Set
        </button>
      </span>
    </form>
  );
}

/** A plan's blocked step: it raises no notice, so the centre lists it, to open or dismiss (Round 6). */
function BlockedRowView({ r }: { r: BlockedRow }) {
  const waits = blockerOf(r.page);
  return (
    <div className="bell-row is-unread is-blocked">
      <button className="bell-row-main" onClick={() => openPlace({ board: r.board, entry: r.page.id })}>
        <span className="bell-ico">
          <Flag size={14} />
        </span>
        <span className="bell-words">
          <span className="bell-title">{pageTitle(r.page)} is blocked</span>
          {/* what it waits on, in the agent's few words (Plan Board Fixes, 22 Oct) */}
          {waits?.on && <span className="bell-body">{waits.plain ? `Waiting on ${waits.on}` : waits.on}</span>}
          <span className="bell-meta">
            Blocked {sinceWords(r.at)} · {r.boardName}
          </span>
        </span>
        <ChevronRight size={14} className="bell-go" aria-hidden />
      </button>
      <span className="bell-tools">
        <button aria-label={`Dismiss: ${pageTitle(r.page)} is blocked`} data-tip="Dismiss" onClick={() => dismiss([r.key])}>
          <X size={13} />
        </button>
      </span>
    </div>
  );
}

/** The last 30 days' dismissed notifications, each to bring back. */
function DismissedList() {
  const sig = useStore((s) => dismissedRows(s).map((r) => `${r.key}@${r.at}`).join());
  const rows = useMemo(() => dismissedRows(get()), [sig]);
  if (!rows.length) return <p className="bell-none">Nothing dismissed in the last 30 days.</p>;
  return (
    <>
      {rows.map((r) => (
        <div key={r.key} className="bell-row is-dismissed">
          <span className="bell-row-main">
            <span className="bell-words">
              <span className="bell-title">{boardWords(r.title)}</span>
              <span className="bell-meta">
                Dismissed {dayOf(r.at)}, {timeOf(r.at)}
                {r.where ? ` · ${r.where}` : ""}
              </span>
            </span>
          </span>
          <span className="bell-tools is-shown">
            <button className="btn btn-xs" onClick={() => undismiss([r.key])}>
              Bring back
            </button>
          </span>
        </div>
      ))}
    </>
  );
}

export function Bell() {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  // the Dismissed view, in place of the list (Round 6)
  const [past, setPast] = useState(false);
  const unread = useUnread();
  const notices = useStore(useShallow((s) => shownNotices(s)));
  // rows made fresh on each read: the store is watched through a signature, so the list draws only when it changes
  const blockedSig = useStore((s) => blockedRows(s).map((r) => `${r.key}@${r.at}`).join());
  const blocked = useMemo(() => blockedRows(get()), [blockedSig]);
  // what the Dismissed view shows: the last 30 days
  const dismissedCount = useStore((s) => dismissedRows(s).length);
  const reminders = useStore((s) => s.reminders);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("pointerdown", close, true);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("pointerdown", close, true);
      window.removeEventListener("keydown", key);
    };
  }, [open]);
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const today = notices.filter((n) => n.t >= midnight.getTime());
  const earlier = notices.filter((n) => n.t < midnight.getTime());
  return (
    <div className="bell" ref={ref}>
      <button className={cx("bell-btn island", open && "is-on")} onClick={() => setOpen(!open)} aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"} aria-expanded={open} data-tip={open ? undefined : "Notifications"}>
        <BellIcon size={15} />
        {unread > 0 && <CountBadge n={unread} label={`${unread} unread`} />}
      </button>
      {open && (
        <div className="bell-panel island" role="dialog" aria-label="Notifications">
          <div className="bell-head">
            <b>Notifications</b>
            <span className="bell-head-tools">
              <button aria-label="Set a reminder" data-tip="Set a reminder" className={cx(adding && "is-on")} onClick={() => setAdding(!adding)}>
                <Plus size={14} />
              </button>
              <button aria-label="Mark all read" data-tip="Mark all read" disabled={!unread} onClick={() => markRead({ all: true })}>
                <CheckCheck size={14} />
              </button>
              <button aria-label="Dismiss all" data-tip="Dismiss all" disabled={!notices.length && !blocked.length} onClick={() => dismiss(allShownKeys(get()), "Dismissed everything")}>
                <Trash2 size={14} />
              </button>
            </span>
          </div>
          {adding && <RemindMe onDone={() => setAdding(false)} />}
          {past ? (
            <div className="bell-list">
              <p className="bell-sec">Dismissed, last 30 days</p>
              <DismissedList />
            </div>
          ) : (
          <div className="bell-list">
            {!notices.length && !blocked.length && <p className="bell-none">Nothing yet. Reminders you set, and what comes due on your boards, show here.</p>}
            {blocked.length > 0 && <p className="bell-sec">Blocked steps</p>}
            {blocked.map((r) => (
              <BlockedRowView key={r.key} r={r} />
            ))}
            {today.length > 0 && <p className="bell-sec">Today</p>}
            {today.map((n) => (
              <Row key={n.id} n={n} />
            ))}
            {earlier.length > 0 && <p className="bell-sec">Earlier</p>}
            {earlier.map((n) => (
              <Row key={n.id} n={n} />
            ))}
            {reminders.length > 0 && <p className="bell-sec">Coming up</p>}
            {reminders.slice(0, 20).map((r) => (
              <div key={r.id} className="bell-row is-coming">
                <span className="bell-row-main">
                  <span className="bell-ico">
                    <AlarmClock size={14} />
                  </span>
                  <span className="bell-words">
                    <span className="bell-title">
                      <Linked text={r.text} />
                    </span>
                    <span className="bell-meta">
                      {dayOf(r.at)}, {timeOf(r.at)}
                      {r.repeat ? ` · ${r.repeat}` : ""}
                      {r.by ? ` · set by ${r.by}` : ""}
                    </span>
                  </span>
                </span>
                <span className="bell-tools">
                  <button aria-label={`Cancel the reminder ${r.text}`} data-tip="Cancel" onClick={() => cancelReminder(r.id)}>
                    <X size={13} />
                  </button>
                </span>
              </div>
            ))}
          </div>
          )}
          <span className="bell-feet">
            <button className={cx("bell-foot", past && "is-on")} onClick={() => setPast(!past)} aria-pressed={past}>
              {past ? "Back to notifications" : `Dismissed${dismissedCount ? ` · ${dismissedCount}` : ""}`}
            </button>
            <button className="bell-foot" onClick={() => (setOpen(false), set({ panel: "settings", panelArg: { tab: "notifications" } }))}>
              Notification settings
            </button>
          </span>
        </div>
      )}
    </div>
  );
}
